import ac from 'argument-contracts';
import { coerceArray } from '@meltwater/coerce';
import { DateRangeTypes, DurationUnits } from '@meltwater/date-range/cjs';
import { Filter } from '@meltwater/filters';
import { InputId } from '@meltwater/inputs';
import moment from 'moment-timezone';
import utcTzRegex from '../utc-tz-regex';

/**
 * A serialized representation of a the data in a single namespace of an AppletHost. This is intended for network communication.
 *
 * @param {object} options - See below
 * @param {string} options.coordinatedQueryTime - The time to which all queries from the AppletHost are being synchronized. Must be formatted as yyyy-mm-ddTmm:ss±hh:mm
 * @param {number} [duration] - The number of durationUnits for the DateRange
 * @param {DurationUnits} [durationUnits] - The durationUnit for the DateRange. Must be a valid @meltwater/date-range DurationUnits value
 * @param {string} [options.endDate] - The date range end. Must be formatted as yyyy-mm-ddTmm:ss±hh:mm. Optional unless rangeType is CUSTOM_RANGE.
 * @param {Filter[]} options.filters - The filters as an array of Filter objects which can each be coerced to a @meltwater/filters Filter
 * @param {string[]} options.inputs - The inputs as an array of strings which can each be parsed as a @meltwater/inputs InputId
 * @param {DateRangeTypes} options.rangeType - The date range type. Must be valid @meltwater/date-range DateRangeTypes value
 * @param {string} [options.startDate] - The date range start. Must be formatted as yyyy-mm-ddTmm:ss±hh:mm. Optional unless rangeType is CUSTOM_RANGE.
 * @param {string} options.timezone - A valid moment timezone
 *
 * @prop {number} duration - The number of durationUnits for the DateRange
 * @prop {DurationUnits} durationUnits - The durationUnit for the DateRange. Must be a valid @meltwater/date-range DurationUnits value
 * @prop {string} [endDate] - The date range end. Must be formatted as yyyy-mm-ddTmm:ss±hh:mm. Optional unless rangeType is CUSTOM_RANGE.
 * @prop {string[]} inputs - The inputs as an array of strings which can be parsed as a @meltwater/inputs InputId
 * @prop {string} noNewDocsAfter - The time after which any newly indexed results should be excluded. This is set of the coordinatedQueryTime parameter.
 * @prop {DateRangeTypes} rangeType - The date range type. Must be valid @meltwater/date-range DateRangeTypes value
 * @prop {string} [startDate] - The date range start. Must be formatted as yyyy-mm-ddTmm:ss±hh:mm. Optional unless rangeType is CUSTOM_RANGE.
 * @prop {string} timezone - A valid moment timezone
 */
export class AppletHostSerialization {
    constructor (options) {
        if (!DateRangeTypes.isValidRangeType(options.rangeType)) {
            throw new TypeError(`rangeType must be a valid DateRangeType. Provided value: ${options.rangeType}`);
        }

        if (moment.tz.zone(options.timezone) === null) {
            throw new TypeError(`timezone must be a valid moment timezone. Provided value: ${options.timezone}`);
        }

        if (!utcTzRegex.test(options.coordinatedQueryTime)) {
            throw new TypeError(`coordinatedQueryTime must be formatted as yyyy-mm-ddTmm:ss±hh:mm. Provided value: ${options.coordinatedQueryTime}`);
        }

        ac.assertArray(options.inputs, 'inputs');
        options.inputs.forEach(input => InputId.parse(input));

        this.coordinatedQueryTime = options.coordinatedQueryTime;
        this.filters = coerceArray(options.filters, Filter, `filters must be an array of objects that can be coerced to Filter. Provided value: ${JSON.stringify(options.filters)}`);
        this.inputs = options.inputs;
        this.noNewDocsAfter = options.coordinatedQueryTime;
        this.rangeType = options.rangeType;
        this.timezone = options.timezone;

        if (this.rangeType === DateRangeTypes.CUSTOM_RANGE) {
            if (!utcTzRegex.test(options.startDate)) {
                throw new TypeError(`startDate must be formatted as yyyy-mm-ddTmm:ss±hh:mm. Provided value: ${options.startDate}`);
            }

            if (!utcTzRegex.test(options.endDate)) {
                throw new TypeError(`endDate must be formatted as yyyy-mm-ddTmm:ss±hh:mm. Provided value: ${options.endDate}`);
            }

            this.endDate = options.endDate;
            this.startDate = options.startDate;
        }

        if (options.durationUnits) {
            if (!DurationUnits.isValid(options.durationUnits)) {
                throw new TypeError(`durationUnits must be a valid DurationUnits. Provided: ${JSON.stringify(options.durationUnits, null, 2)}`);
            }
        }

        this.durationUnits = options.durationUnits;

        if (options.duration) {
            ac.assertNumber(options.duration, 'duration');
        }

        this.duration = options.duration;

        Object.freeze(this);
    }
}

